import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Moment from "moment";
import swal from "sweetalert";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import "./zReport.css";
import "../order/ManageOrderFilters.css";

const lineId = (row) => row.line_ID ?? row.lineId ?? row.Line_ID;

const emptyDraft = () => ({ physical: "", remarks: "", staff: "" });
const asNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const fmtQty = (value) => asNumber(value).toFixed(3);
const fmtMoney = (value) =>
  value == null || value === ""
    ? "—"
    : Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });

const ReconciliationReportDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const headerId = parseInt(id, 10);
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [rowDrafts, setRowDrafts] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lineSearch, setLineSearch] = useState("");
  const [appliedLineSearch, setAppliedLineSearch] = useState("");
  const [headerNotes, setHeaderNotes] = useState("");
  const [lineSortOrder, setLineSortOrder] = useState({ value: "ASC", label: "ASC" });
  const [savingLineId, setSavingLineId] = useState(null);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCompID, setSelectedCompID] = useState(searchParams.get("compId") || "");

  const invalidId = !Number.isFinite(headerId) || headerId <= 0;
  const effectiveCompID = isMain ? Number(selectedCompID) || 0 : Number(CompID) || 0;

  const toInt = (value) => {
    const n = typeof value === "number" ? value : parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    const baseCompId = toInt(CompID);
    if (baseCompId && (!selectedCompID || Number(selectedCompID) <= 0)) {
      setSelectedCompID(String(baseCompId));
    }
  }, [CompID, selectedCompID]);

  useEffect(() => {
    const fetchLocations = async () => {
      if (!isMain || !CompID) return;
      try {
        const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, { params: { CompID } });
        const locationData = response.data?.data ?? response.data ?? [];
        const arr = Array.isArray(locationData) ? locationData : [];
        const baseCompId = toInt(CompID);
        if (!baseCompId) return;
        const parentName = profileData?.compName ?? profileData?.CompName ?? `Company ${baseCompId}`;
        const base = [{ value: baseCompId, label: parentName }];
        const branches = arr
          .filter((loc) => {
            const id = toInt(loc.CompID ?? loc.compID);
            const pid = toInt(loc.ParentComID ?? loc.parentComID);
            return id && pid === baseCompId && id !== baseCompId;
          })
          .map((loc) => {
            const id = toInt(loc.CompID ?? loc.compID);
            const name = String(loc.CompName ?? loc.compName ?? "").trim();
            return { value: id, label: name || `Branch ${id}` };
          })
          .filter((loc) => loc.value);
        setLocations([...base, ...branches]);
      } catch {
        setLocations([]);
      }
    };
    fetchLocations();
  }, [CompID, isMain, profileData]);

  const loadHeader = useCallback(async () => {
    if (!effectiveCompID || invalidId) return;
    const res = await axiosInstance.get(`${APIs.GET_STOCK_RECONCILIATION_BY_ID(headerId)}`, { params: { CompID: effectiveCompID } });
    if (res.data?.status && res.data.data) {
      setHeader(res.data.data);
      setHeaderNotes(res.data.data.notes || "");
    } else {
      showErrorToast(res.data?.message || "Not found");
    }
  }, [effectiveCompID, headerId, invalidId]);

  const loadLines = useCallback(
    async (opts) => {
      if (!effectiveCompID || invalidId) return;
      const silent = opts?.silent === true;
      if (!silent) setLoading(true);
      try {
        const res = await axiosInstance.get(APIs.GET_STOCK_RECONCILIATION_LINES(headerId), {
          params: {
            CompID: effectiveCompID,
            Search: appliedLineSearch?.trim() || "",
            SortBy: "MenuItem_Name",
            SortOrder: lineSortOrder?.value ?? lineSortOrder,
            PageNumber: pageNumber,
            PageSize: pageSize?.value ?? pageSize,
          },
        });
        if (res.data?.status) {
          const data = res.data.data || [];
          setLines(data);
          setSelectedLineIds((prev) => prev.filter((id) => data.some((r) => lineId(r) === id)));
          setTotalRecords(res.data.totalRecords ?? 0);
          setTotalPages(res.data.totalPages ?? 0);
          setRowDrafts((prev) => {
            const next = { ...prev };
            data.forEach((r) => {
              const lid = lineId(r);
              if (next[lid] === undefined) {
                next[lid] = {
                  physical: r.physicalStock != null && r.physicalStock !== "" ? String(r.physicalStock) : "",
                  remarks: r.remarks ?? "",
                  staff: r.responsibleStaffUserName ?? r.ResponsibleStaffUserName ?? "",
                };
              }
            });
            return next;
          });
        } else {
          showErrorToast(res.data?.message || "Failed to load lines");
        }
      } catch (e) {
        showErrorToast("Failed to load lines");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [effectiveCompID, headerId, invalidId, appliedLineSearch, pageNumber, pageSize, lineSortOrder]
  );

  useEffect(() => {
    loadHeader();
  }, [loadHeader]);

  useEffect(() => {
    loadLines();
  }, [loadLines]);

  useEffect(() => {
    const loadStaff = async () => {
      if (!effectiveCompID) return;
      try {
        const res = await axiosInstance.get(APIs.GET_ALL_STAFF, { params: { CompID: effectiveCompID } });
        if (res.data?.status && Array.isArray(res.data.data)) {
          setStaffOptions(res.data.data);
        }
      } catch {
        /* optional */
      }
    };
    loadStaff();
  }, [effectiveCompID]);

  const updateDraft = (lid, field, value) => {
    setRowDrafts((prev) => ({
      ...prev,
      [lid]: { ...emptyDraft(), ...prev[lid], [field]: value },
    }));
  };

  const buildPatchBody = (d) => {
    const body = {
      remarks: d.remarks ?? "",
      responsibleStaffUserName: d.staff || null,
      clearResponsibleStaffUserName: !d.staff,
    };
    if (d.physical !== "" && d.physical != null) {
      const ps = parseFloat(d.physical);
      if (!Number.isNaN(ps)) body.physicalStock = ps;
    }
    return body;
  };

  const handleUpdateSingleLine = async (row) => {
    if (!effectiveCompID || invalidId) return;
    if (!editable) {
      showErrorToast("This reconciliation is read-only.");
      return;
    }
    const lid = lineId(row);
    const draft = rowDrafts[lid] || emptyDraft();
    setSavingLineId(lid);
    try {
      const res = await axiosInstance.patch(`${APIs.PATCH_STOCK_RECONCILIATION_LINE(lid)}?CompID=${effectiveCompID}`, buildPatchBody(draft));
      if (res.data?.status) {
        showSuccessToast("Line updated");
        await loadLines({ silent: true });
      } else {
        showErrorToast(res.data?.message || "Failed to update line");
      }
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Failed to update line");
    } finally {
      setSavingLineId(null);
    }
  };

  const handleAdjustLineStock = async (row) => {
    if (!effectiveCompID || invalidId) return;
    const lid = lineId(row);
    const alreadyAdjusted = row.isStockAdjusted || row.IsStockAdjusted;
    if (alreadyAdjusted) return;
    setSavingLineId(lid);
    try {
      const res = await axiosInstance.post(`${APIs.POST_STOCK_RECONCILIATION_ADJUST_LINE(lid)}?CompID=${effectiveCompID}`);
      if (res.data?.status) {
        showSuccessToast("Stock adjusted");
        await loadLines({ silent: true });
      } else {
        showErrorToast(res.data?.message || "Failed to adjust stock");
      }
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Failed to adjust stock");
    } finally {
      setSavingLineId(null);
    }
  };

  const toggleLineSelection = (lid, checked) => {
    setSelectedLineIds((prev) => {
      if (checked) return prev.includes(lid) ? prev : [...prev, lid];
      return prev.filter((x) => x !== lid);
    });
  };

  const selectableOnPage = lines
    .filter((r) => {
      const alreadyAdjusted = r.isStockAdjusted || r.IsStockAdjusted;
      const diff = asNumber(r.difference);
      const hasDifference = Math.abs(diff) > 0.0000001;
      return !alreadyAdjusted && hasDifference;
    })
    .map((r) => lineId(r));

  const allSelectableChecked =
    selectableOnPage.length > 0 && selectableOnPage.every((id) => selectedLineIds.includes(id));

  const handleAdjustSelectedLines = async () => {
    if (!effectiveCompID || invalidId) return;
    if (selectedLineIds.length === 0) {
      showErrorToast("Please select at least one line.");
      return;
    }

    const ok = await swal({
      title: "Adjust selected lines?",
      text: `This will apply stock adjustment for ${selectedLineIds.length} selected line(s).`,
      icon: "warning",
      buttons: ["Cancel", "Adjust"],
      dangerMode: true,
    });
    if (!ok) return;

    setLoading(true);
    try {
      const results = await Promise.allSettled(
        selectedLineIds.map((lid) =>
          axiosInstance.post(`${APIs.POST_STOCK_RECONCILIATION_ADJUST_LINE(lid)}?CompID=${effectiveCompID}`)
        )
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled" && r.value?.data?.status
      ).length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        showSuccessToast(`${successCount} line(s) adjusted successfully.`);
      }
      if (failCount > 0) {
        showErrorToast(`${failCount} line(s) could not be adjusted.`);
      }

      setSelectedLineIds([]);
      await loadLines({ silent: true });
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Failed to adjust selected lines.");
    } finally {
      setLoading(false);
    }
  };

  const saveHeaderAndLines = async () => {
    if (!effectiveCompID || invalidId) return { ok: false };
    const st = (header?.status || "").toLowerCase();
    if (!["draft", "inprogress"].includes(st)) {
      showErrorToast("This reconciliation is read-only.");
      return { ok: false };
    }
    try {
      const hRes = await axiosInstance.put(`${APIs.PUT_STOCK_RECONCILIATION_HEADER(headerId)}?CompID=${effectiveCompID}`, {
        notes: headerNotes,
      });
      if (!hRes.data?.status) {
        showErrorToast(hRes.data?.message || "Failed to save header");
        return { ok: false };
      }
      setHeader(hRes.data.data);

      const entries = Object.entries(rowDrafts);
      const results = await Promise.allSettled(
        entries.map(([idStr, d]) =>
          axiosInstance
            .patch(`${APIs.PATCH_STOCK_RECONCILIATION_LINE(Number(idStr))}?CompID=${effectiveCompID}`, buildPatchBody(d))
            .then((res) => {
              if (!res.data?.status) throw new Error(res.data?.message || "Line update failed");
              return res;
            })
        )
      );
      const failed = results.filter(
        (r) => r.status === "rejected" || (r.status === "fulfilled" && r.value?.data?.status === false)
      );
      if (failed.length > 0) {
        showErrorToast("Some lines failed to save. Check your network and try again.");
        return { ok: false };
      }
      await loadLines({ silent: true });
      return { ok: true };
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Save failed");
      return { ok: false };
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      const { ok } = await saveHeaderAndLines();
      if (ok) showSuccessToast("Saved");
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshToNow = async () => {
    if (!effectiveCompID || invalidId) return;
    const confirmed = await swal({
      title: "Refresh to current time?",
      text: "This will update system movement and sold quantities up to now, and add any new stock-tracked items that were created or sold after this draft was started. Entered physical stock, remarks, and responsible staff will be preserved.",
      icon: "warning",
      buttons: ["Cancel", "Refresh"],
      dangerMode: false,
    });
    if (!confirmed) return;

    setLoading(true);
    try {
      const { ok } = await saveHeaderAndLines();
      if (!ok) return;

      const res = await axiosInstance.post(
        `${APIs.POST_STOCK_RECONCILIATION_REFRESH_TO_NOW(headerId)}?CompID=${effectiveCompID}`
      );
      if (res?.data?.status) {
        setHeader(res.data.data);
        showSuccessToast("Reconciliation refreshed. Movements updated and any new stock items added.");
        await loadHeader();
        await loadLines({ silent: true });
      } else {
        showErrorToast(res?.data?.message || "Refresh failed.");
      }
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Refresh failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!effectiveCompID || invalidId) return;
    const submitConfirmed = await swal({
      title: "Submit reconciliation?",
      text: "Submit will save all entered data and finalize this reconciliation. If Physical Stock is blank on any line, it will be set equal to Current Stock.",
      icon: "warning",
      buttons: ["Cancel", "Continue"],
      dangerMode: false,
    });
    if (!submitConfirmed) return;
    setLoading(true);
    try {
      const { ok } = await saveHeaderAndLines();
      if (!ok) return;
      const res = await axiosInstance.post(`${APIs.POST_STOCK_RECONCILIATION_FINALIZE(headerId)}?CompID=${effectiveCompID}`, {
        autoFillMissingPhysical: true,
      });

      if (res?.data?.status) {
        showSuccessToast("Reconciliation submitted");
        setHeader(res.data.data);
        await loadLines({ silent: true });
      } else {
        showErrorToast(res?.data?.message || "Submit failed.");
      }
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Submit failed.");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!effectiveCompID || invalidId) return;
    if (!window.confirm("Cancel this draft?")) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post(`${APIs.POST_STOCK_RECONCILIATION_CANCEL(headerId)}?CompID=${effectiveCompID}`);
      if (res.data?.status) {
        showSuccessToast("Cancelled");
        setHeader(res.data.data);
      } else showErrorToast(res.data?.message || "Cancel failed");
    } catch (e) {
      showErrorToast(e.response?.data?.message || "Cancel failed");
    } finally {
      setLoading(false);
    }
  };

  const staffName = (s) =>
    [s.firstName || s.FirstName, s.lastName || s.LastName].filter(Boolean).join(" ") || s.userName || s.UserName || s.email || s.Email;

  const editable = ["draft", "inprogress"].includes((header?.status || "").toLowerCase());

  const titleText = useMemo(() => {
    if (!header) return "";
    const n = headerNotes?.trim() || header.notes?.trim();
    if (n) return n;
    return `Reconciliation #${headerId}`;
  }, [header, headerNotes, headerId]);

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    let cls = "bg-secondary";
    if (s === "finalized") cls = "bg-success";
    else if (s === "draft") cls = "bg-primary";
    else if (s === "inprogress") cls = "bg-info text-dark";
    else if (s === "cancelled") cls = "bg-dark";
    return <span className={`badge ${cls}`}>{status}</span>;
  };

  if (invalidId) {
    return (
      <div className="p-4">
        <p>Invalid reconciliation.</p>
        <Link to={`/report/reconciliationReport?compId=${effectiveCompID || ""}`}>Back to list</Link>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/report/reconciliationReport?compId=${effectiveCompID || ""}`}>Stock Adjustments</Link>
            </li>
            <li className="breadcrumb-item active">#{headerId}</li>
          </ol>
          <h1 className="page-header mb-0">Stock reconciliation</h1>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
          {editable && (
            <>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleSaveDraft}
                disabled={loading}
              >
                <i className="fa fa-save me-2" />
                Save draft
              </button>
              <button
                type="button"
                className="btn btn-outline-info"
                onClick={handleRefreshToNow}
                disabled={loading}
              >
                <i className="fa fa-rotate-right me-2" />
                Refresh to now
              </button>
              <button
                type="button"
                className="btn px-4 text-white"
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  boxShadow: `0 2px 8px ${themeColor}40`,
                }}
              >
                <i className="fa fa-check me-2" />
                Submit reconciliation
              </button>
            </>
          )}
          {String(header?.status || "").toLowerCase() === "finalized" && selectedLineIds.length > 0 && (
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={handleAdjustSelectedLines}
              disabled={loading}
            >
              <i className="fa fa-wand-magic-sparkles me-2" />
              Adjust selected ({selectedLineIds.length})
            </button>
          )}
          <Link to={`/report/reconciliationReport?compId=${effectiveCompID || ""}`} className="btn btn-outline-secondary">
            Back to list
          </Link>
        </div>
      </div>

      {header && (
        <div
          className="card border-0 mb-4 text-white"
          style={{
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}cc 100%)`,
            borderRadius: "12px",
            boxShadow: `0 4px 20px ${themeColor}40`,
          }}
        >
          <div className="card-body p-4">
            <div className="row align-items-center g-3">
              <div className="col-lg-5">
                <div className="small text-white-50 mb-1">Reconciliation name</div>
                {editable ? (
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    style={{ color: "#212529" }}
                    value={headerNotes}
                    onChange={(e) => setHeaderNotes(e.target.value)}
                    placeholder="Name this count session"
                  />
                ) : (
                  <div className="fs-5 fw-semibold">{titleText}</div>
                )}
              </div>
              <div className="col-lg-4">
                <div className="small text-white-50 mb-1">Date</div>
                <div className="fw-semibold">
                  {header.createdAt ? Moment(header.createdAt).format("DD MMM YYYY, HH:mm") : "—"}
                </div>
                <div className="small mt-1 opacity-75">
                  Period:{" "}
                  {header.periodFromDateTime ? Moment(header.periodFromDateTime).format("DD MMM YYYY HH:mm") : "—"} →{" "}
                  {header.periodToDateTime ? Moment(header.periodToDateTime).format("DD MMM YYYY HH:mm") : "—"}
                </div>
              </div>
              <div className="col-lg-3 text-lg-end">
                <div className="small text-white-50 mb-1">Status</div>
                <div className="fs-6">{statusBadge(header.status)}</div>
                <div className="small mt-2 opacity-75">
                  {header.lineCount ?? header.LineCount ?? 0} line(s)
                </div>
              </div>
            </div>
            {editable && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-outline-light btn-sm" onClick={cancel}>
                  Cancel draft
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card border-0">
        <div className="card-body p-3">
          <h5 className="mb-3">Line items — physical vs system stock</h5>
          <div className="mb-4">
            <div
              className="card filter-card"
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                borderRadius: "12px",
                border: "none",
                boxShadow: `0 4px 20px ${themeColor}40`,
              }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-center">
                  {isMain && (
                    <div className="col-xl-3 col-lg-3 col-md-6">
                      <select
                        className="form-control filter-input"
                        value={selectedCompID}
                        onChange={(e) => {
                          setSelectedCompID(e.target.value);
                          setPageNumber(1);
                        }}
                        style={{
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          color: "#333",
                          height: "42px",
                        }}
                      >
                        {locations.map((loc) => (
                          <option key={loc.value} value={loc.value}>
                            {loc.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-xl-4 col-lg-4 col-md-6">
                    <input
                      type="text"
                      className="filter-input w-100"
                      placeholder="Search item name, SKU, or ID..."
                      value={lineSearch}
                      onChange={(e) => setLineSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setPageNumber(1);
                          setAppliedLineSearch(lineSearch);
                        }
                      }}
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        color: "#333",
                        borderRadius: "8px",
                        padding: "10px 12px",
                      }}
                    />
                  </div>
                  <div className="col-xl-2 col-lg-2 col-md-6">
                    <SortOrderDropDownSelection
                      defaultValue={lineSortOrder}
                      onChange={(v) => {
                        setLineSortOrder(v);
                        setPageNumber(1);
                      }}
                    />
                  </div>
                  <div className="col-xl-2 col-lg-2 col-md-6">
                    <button
                      type="button"
                      className="btn w-100 text-white"
                      style={{
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: "8px",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setPageNumber(1);
                        setAppliedLineSearch(lineSearch);
                      }}
                    >
                      <i className="fa fa-search me-1" />
                      Search
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="table-responsive table-container"
            style={{
              minHeight: "200px",
              maxHeight: "480px",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <table className="table table-hover table-bordered">
              <thead
                className="sticky-header"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  color: isDarkMode ? "#fff" : "#000",
                }}
              >
                <tr>
                  <th className="text-center" style={{ width: 36 }}>
                    {String(header?.status || "").toLowerCase() === "finalized" ? (
                      <input
                        type="checkbox"
                        checked={allSelectableChecked}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelectedLineIds((prev) => Array.from(new Set([...prev, ...selectableOnPage])));
                          } else {
                            setSelectedLineIds((prev) => prev.filter((id) => !selectableOnPage.includes(id)));
                          }
                        }}
                      />
                    ) : null}
                  </th>
                  <th>Item</th>
                  <th className="text-end">Prev. physical</th>
                  <th className="text-end">PO in</th>
                  <th className="text-end">Req. in</th>
                  <th className="text-end">Req. out</th>
                  <th className="text-end">Manual Adj.</th>
                  <th className="text-end">Sold</th>
                  <th className="text-end">Current Stock</th>
                  <th className="text-end">Physical</th>
                  <th className="text-end">Diff</th>
                  <th className="text-end">Unit Cost</th>
                  <th className="text-end">Difference Cost</th>
                  <th>Remarks</th>
                  <th>Responsible</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((row) => {
                  const lid = lineId(row);
                  const d = rowDrafts[lid] || emptyDraft();
                  const systemQty = asNumber(row.currentSystemStock);
                  const parsedPhysical = d.physical === "" ? null : Number(d.physical);
                  const liveDiff = Number.isFinite(parsedPhysical)
                    ? parsedPhysical - systemQty
                    : row.difference != null
                      ? asNumber(row.difference)
                      : null;
                  const hasDifference = liveDiff != null && Math.abs(liveDiff) > 0.0000001;
                  const manualNet = asNumber(row.qtyManualAdjustedIn) - asNumber(row.qtyManualAdjustedOut);
                  const manualNetColor = manualNet > 0 ? "#198754" : manualNet < 0 ? "#dc3545" : undefined;
                  const unitCostPrice = row.unitCostPrice ?? row.UnitCostPrice;
                  const differenceCost = row.differenceCost ?? row.DifferenceCost;
                  const diffColor =
                    liveDiff == null ? undefined : liveDiff > 0 ? "#198754" : liveDiff < 0 ? "#dc3545" : "#0d6efd";
                  return (
                    <tr key={lid}>
                      <td className="text-center">
                        {String(header?.status || "").toLowerCase() === "finalized" ? (
                          <input
                            type="checkbox"
                            disabled={(row.isStockAdjusted || row.IsStockAdjusted) || !hasDifference}
                            checked={selectedLineIds.includes(lid)}
                            onChange={(e) => toggleLineSelection(lid, e.target.checked)}
                          />
                        ) : null}
                      </td>
                      <td>
                        <div className="fw-semibold">{row.menuItem_Name}</div>
                        {row.menuItem_SKU ? <div className="text-muted small">SKU {row.menuItem_SKU}</div> : null}
                      </td>
                      <td className="text-end" style={{ backgroundColor: "#f8fafc" }}>
                        {row.previousPhysicalStock != null ? fmtQty(row.previousPhysicalStock) : "—"}
                      </td>
                      <td className="text-end" style={{ backgroundColor: "#f8fafc" }}>{fmtQty(row.qtyAddedPO)}</td>
                      <td className="text-end" style={{ backgroundColor: "#f8fafc" }}>{fmtQty(row.qtyAddedRequisition)}</td>
                      <td className="text-end" style={{ backgroundColor: "#f8fafc" }}>{fmtQty(row.qtyRequisitionOut)}</td>
                      <td className="text-end fw-semibold" style={{ backgroundColor: "#f8fafc", color: manualNetColor }}>
                        {fmtQty(manualNet)}
                      </td>
                      <td className="text-end" style={{ backgroundColor: "#f8fafc" }}>{fmtQty(row.qtySoldSinceLast)}</td>
                      <td className="text-end fw-semibold" style={{ backgroundColor: "#f5f8fb" }}>{fmtQty(systemQty)}</td>
                      <td className="text-end" style={{ minWidth: 92 }}>
                        <input
                          type="number"
                          step="0.001"
                          className="form-control form-control-sm text-end fw-semibold"
                          disabled={!editable}
                          value={d.physical}
                          onChange={(e) => updateDraft(lid, "physical", e.target.value)}
                          style={{ maxWidth: 88, marginLeft: "auto" }}
                        />
                      </td>
                      <td className="text-end fw-semibold" style={{ color: diffColor }}>
                        {liveDiff != null ? fmtQty(liveDiff) : "—"}
                      </td>
                      <td className="text-end">{fmtMoney(unitCostPrice)}</td>
                      <td className="text-end fw-semibold" style={{ color: diffColor }}>
                        {fmtMoney(differenceCost)}
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <input
                          className="form-control form-control-sm"
                          disabled={!editable}
                          value={d.remarks}
                          onChange={(e) => updateDraft(lid, "remarks", e.target.value)}
                        />
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <select
                          className="form-select form-select-sm"
                          disabled={!editable}
                          value={d.staff}
                          onChange={(e) => updateDraft(lid, "staff", e.target.value)}
                        >
                          <option value="">—</option>
                          {staffOptions.map((s, idx) => {
                            const un = s.userName || s.UserName;
                            return (
                              <option key={un || idx} value={un}>
                                {staffName(s)}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                      <td className="text-center" style={{ minWidth: 82 }}>
                        {editable ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => handleUpdateSingleLine(row)}
                            disabled={savingLineId === lid}
                            title="Update this line"
                            style={{
                              width: "34px",
                              height: "30px",
                              borderRadius: "6px",
                              border: `1px solid ${themeColor}`,
                              backgroundColor: savingLineId === lid ? `${themeColor}22` : "transparent",
                              color: themeColor,
                            }}
                          >
                            <i className={`fa ${savingLineId === lid ? "fa-spinner fa-spin" : "fa-save"}`} />
                          </button>
                        ) : (row.isStockAdjusted || row.IsStockAdjusted) ? (
                          <span className="badge bg-success">Adjusted</span>
                        ) : String(header?.status || "").toLowerCase() === "finalized" && hasDifference ? (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => handleAdjustLineStock(row)}
                            disabled={savingLineId === lid}
                            title="Adjust stock for this finalized line"
                            style={{
                              height: "30px",
                              borderRadius: "6px",
                              border: "1px solid #198754",
                              backgroundColor: savingLineId === lid ? "#19875422" : "transparent",
                              color: "#198754",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            {savingLineId === lid ? "..." : "Adjust"}
                          </button>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            totalRecords={totalRecords}
            pageSize={pageSize}
            pageNumber={pageNumber}
            totalPages={totalPages}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            themeColor={themeColor}
          />

          {editable && totalRecords > 0 && (
            <p className="text-muted small mt-2 mb-0">
              Use pagination to enter counts for all items, then <strong>Save draft</strong> or <strong>Submit reconciliation</strong>.
              Submit saves your changes and finalizes the report (all lines must have physical stock).
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReconciliationReportDetail;
